import React, {useEffect, useMemo, useState} from 'react';
import { useTranslation } from "react-i18next";
import { useLocation } from 'react-router-dom';
import { AuthConsumer } from 'contexts/auth-context';
import { CQAppConstant } from 'app-constants';
import CQManagedPackageInterstitial from 'components/cq-managed-package-interstitial';
import { isStagingEnvironment, isProductionSalesforceUrl, isValidUrl } from 'utils/environment.utils';
import { getInstallUrl, fetchManagedPackageVersionId } from 'common/utils';
import { IconSettings, Toast } from '@salesforce/design-system-react';

const CQ_LOGO = '/logo512.png';


function CQLoginScreen() {

    const { t } = useTranslation();
    const [ customDomain, setCustomDomain ] = useState<string>('');
    const [selectedLoginUrl, setSelectedLoginUrl] = useState<string | null>(null);
    const [ customDomainError, setCustomDomainError ] = useState<string>('');
    const authContext = AuthConsumer();
    const [isStaging, setIsStaging] = useState<boolean>(false);
    const showInstallInterstitial = !!selectedLoginUrl;

    
    useEffect(() => {
        setIsStaging(isStagingEnvironment());
        // Fetch managed package version ID from backend to cache it
        fetchManagedPackageVersionId().catch(err => console.error('Failed to fetch managed package version ID:', err));
    }, []);
    
    const { search } = useLocation();
    const oauthError = useMemo(() => {
        const errorParam = new URLSearchParams(search).get('error');
        return errorParam ? decodeURIComponent(errorParam) : '';
    }, [search]);
    const [showError, setShowError] = useState<boolean>(false);

    useEffect(() => {
        if (!oauthError) {
            return;
        }
        setShowError(true);
        window.history.replaceState({}, document.title, window.location.pathname);
    }, [oauthError]);

    useEffect(() => {
        if (!showError) {
            return;
        }
        const timeoutId = window.setTimeout(() => setShowError(false), 10000);
        return () => window.clearTimeout(timeoutId);
    }, [showError]);


    /**
     * Handles the "Install Now" button click by opening the Salesforce package installation URL in a new tab.
     * 
     * @returns {void}
     */
    const handleInstallNow = () => {
        if (!selectedLoginUrl) return;
        try {
            const installUrl = getInstallUrl(selectedLoginUrl);
            window.open(installUrl, '_blank', 'noopener,noreferrer');
        } catch (error) {
            alert(t('loginpage_install_managed_package_error'));
            console.error('Install URL error:', error);
        }
    };

    /**
     * Handles the "Already Installed - Continue" button click by proceeding with OAuth authentication.
     * 
     * @returns {void}
     */
    const handleContinue = () => {
        if (!selectedLoginUrl) return;
        authenticate(selectedLoginUrl);
    };

    /**
     * Handles the "Back" button click by returning to the initial login options screen.
     * 
     * @returns {void}
     */
    const handleBack = () => setSelectedLoginUrl(null);

    /**
     * Initiates the OAuth authentication process using the provided Salesforce login URL.
     *
     * Constructs a redirect URL based on the current window location, then triggers
     * the backend OAuth flow. On successful authentication, the backend sets
     * authentication cookies and redirects back to the UI.
     *
     * @param {string} salesforceLoginUrl - The Salesforce OAuth login URL (e.g., login.salesforce.com, test.salesforce.com)
     * @returns {void}
    */
    const authenticate = (salesforceLoginUrl: string) => {
        // Use window.location.origin to properly handle standard ports (80/443)
        const backendBaseUrl = window.location.host !== CQAppConstant.LOCAL_HOST_NAME
            ? window.location.origin
            : CQAppConstant.LOCAL_SERVER;

        const oauthParams = new URLSearchParams();
        oauthParams.set(CQAppConstant.OAUTH_QUERY_PARAMS.LOGIN_URL, salesforceLoginUrl);
        oauthParams.set(CQAppConstant.OAUTH_QUERY_PARAMS.RETURN_TO, CQAppConstant.NAVIGATION.DEFAULT_PATH);

        const oauthStartUrl = `${backendBaseUrl}/oauth2/start?${oauthParams.toString()}`;
        window.location.href = oauthStartUrl;
    }

    /**
     * Validates custom domain URL before authentication.
     * Blocks production URLs when running on staging environment.
     * 
     * @param {string} url - The custom domain URL to validate
     * @returns {void}
     */
    const validateAndAuthenticate = (rawUrl: string) => {
        if (!isValidUrl(rawUrl)) {
            setCustomDomainError(t('loginpage_invalid_custom_domain_url'));
            return;
        }
        if (isStaging && isProductionSalesforceUrl(rawUrl)) {
            setCustomDomainError(t("loginpage_production_blocked_staging"));
            return;
        }
        setCustomDomainError('');
        setSelectedLoginUrl(rawUrl);
    }

    return <>
        { authContext.isAuthenticated ? <></> : 
        <div className="slds-grid slds-grid_vertical slds-grid_vertical-align-center slds-grid_align-center" style={{height: "100vh"}}>
            <div className="slds-col">
                <div className="slds-card slds-var-m-around_large slds-var-p-around_large">
                    <div className="slds-grid slds-grid_vertical login-container">
                        <img src={CQ_LOGO} alt="Logo" className="" />
                        <h2 className="slds-text-heading_medium slds-text-align_center slds-m-vertical_large">{t("loginpage_title")}</h2>

                        {showInstallInterstitial ? (
                            <CQManagedPackageInterstitial
                                onInstallNow={handleInstallNow}
                                onContinue={handleContinue}
                                onBack={handleBack}
                                isVisible={showInstallInterstitial}
                            />
                        ) : (
                            <>
                                {!isStaging && <button className="slds-button slds-button_brand slds-m-vertical_x-small" onClick={() => { validateAndAuthenticate(CQAppConstant.DEFAULT_URLS.SF_LOGIN); }}>{t("loginpage_loginproduction")}</button>}
                                <button className="slds-button slds-button_brand slds-m-vertical_x-small" onClick={() => { validateAndAuthenticate(CQAppConstant.DEFAULT_URLS.SF_TEST); }}>{t("loginpage_loginsandbox")}</button>
                                <input className="slds-input slds-m-vertical_x-small" placeholder={t("loginpage_customdomainplaceholder")} onChange={(evt) => setCustomDomain(evt.target.value)}></input>
                                {!!customDomainError && (
                                    <div className="slds-text-color_error slds-m-bottom_x-small" role="alert">{customDomainError}</div>
                                )}
                                <button
                                    className="slds-button slds-button_brand slds-m-left_none"
                                    onClick={() => {
                                        validateAndAuthenticate(customDomain);
                                    }}
                                >
                                    {t("loginpage_logincustom")}
                                </button>
                            </>
                        )}
                        
                    </div>
                </div>
            </div>
        </div>
        }
        {showError && oauthError ? (
            <IconSettings iconPath="/assets/icons">
                <div role="alert" className="slds-notify_container slds-is-fixed">
                    <Toast
                        variant="error"
                        labels={{ heading: oauthError }}
                        onRequestClose={() => setShowError(false)}
                        style={{ outline: 'none', boxShadow: 'none' }}
                    >
                        {oauthError}
                    </Toast>
                </div>
            </IconSettings>
        ) : null}
    </>
}

export default CQLoginScreen;